import React from 'react';

export type StepperStepStatus = 'pending' | 'current' | 'completed';

export interface StepperStep {
  /** Stable key (typically the step identifier). */
  key: string;
  /** Translated label. */
  label: string;
  status: StepperStepStatus;
  /** When provided, the step label becomes a button. */
  onClick?: () => void;
}

interface StepperProps {
  steps: StepperStep[];
  className?: string;
}

const circleClasses = (status: StepperStepStatus) => {
  const base = 'w-3 h-3 border-2 absolute rounded-full transition-all duration-300';
  switch (status) {
    case 'completed':
      return `${base} bg-primary border-primary`;
    case 'current':
      return `${base} bg-white border-primary ring-4 ring-primary/20`;
    case 'pending':
    default:
      return `${base} bg-white border-gray-300`;
  }
};

const connectorClasses = (status: StepperStepStatus) =>
  status === 'completed'
    ? 'grow border-b-2 border-primary border-solid mt-[5px] transition-all duration-300'
    : 'grow border-b-2 border-gray-400 border-dotted mt-[5px] transition-all duration-300';

const labelClasses = (status: StepperStepStatus, clickable: boolean) => {
  if (status === 'current') return 'mt-6 text-xs whitespace-nowrap font-medium text-gray-900';
  if (clickable) return 'mt-6 text-xs whitespace-nowrap text-gray-700 hover:text-primary cursor-pointer';
  // Non cliquable : on garde la couleur neutre (mêmes codes visuels que le parcours
  // individuel), juste pas de hover ni de curseur pointer.
  return 'mt-6 text-xs whitespace-nowrap text-gray-700 cursor-default';
};

export const Stepper: React.FC<StepperProps> = ({ steps, className = '' }) => {
  return (
    <div className={`flex items-start justify-between w-1/2 mx-auto max-w-screen-md ${className}`}>
      {steps.map((step, index) => {
        const clickable = !!step.onClick && step.status !== 'pending';
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col w-3 h-3 relative items-center">
              <div className={circleClasses(step.status)} />
              <button
                type="button"
                disabled={!clickable}
                onClick={clickable ? step.onClick : undefined}
                className={labelClasses(step.status, clickable)}
              >
                {step.label}
              </button>
            </div>
            {index < steps.length - 1 && <div className={connectorClasses(step.status)} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};
